class BrandSystemShowcase extends HTMLElement {
  connectedCallback() {
    this.scroller = this.querySelector('[data-bss-scroller]');
    if (!this.scroller) return;

    this.originalItems = Array.from(this.scroller.querySelectorAll('[data-bss-item]'));
    this.dots = Array.from(this.querySelectorAll('[data-bss-dot]'));
    if (this.originalItems.length === 0) return;

    this.originalCount = this.originalItems.length;
    this.activeIndex = 0;
    this.canLoop = this.originalCount > 1;

    if (this.canLoop) this.buildClones();
    this.items = Array.from(this.scroller.querySelectorAll('[data-bss-item]'));

    this.dots.forEach((dot, idx) => {
      dot.addEventListener('click', () => this.scrollToLogicalIndex(idx));
    });

    this.scroller.addEventListener('scroll', this.onScroll.bind(this), { passive: true });

    this.setupPointerDrag();
    this.setupWheelToHorizontal();
    this.setupLazyVideo();

    // Start centred on the first original item without animation. Layout for
    // cloned video/iframe elements may not be ready in the first frame, so
    // re-run once more after the first scroll settle.
    requestAnimationFrame(() => this.jumpToOriginalIndex(0));
  }

  // Clone the originals once before and once after the real items so the user
  // can scroll in either direction without hitting an edge. After scroll
  // settles, if the closest item lives in a clone zone we silently rebase
  // scrollLeft to the equivalent middle-copy position — invisible because the
  // clones look identical to the originals.
  buildClones() {
    const leading = this.originalItems.map((item) => this.makeClone(item));
    const trailing = this.originalItems.map((item) => this.makeClone(item));
    // Anchor on the first original so leading clones keep their natural order.
    // (insertBefore(_, firstChild) on each one would reverse them, which makes
    // the left peek beside original-0 a duplicate of itself.)
    const firstOriginal = this.originalItems[0];
    leading.forEach((c) => this.scroller.insertBefore(c, firstOriginal));
    trailing.forEach((c) => this.scroller.appendChild(c));
  }

  makeClone(item) {
    const clone = item.cloneNode(true);
    clone.setAttribute('aria-hidden', 'true');
    clone.setAttribute('data-bss-clone', '');
    clone.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
    // Keep clone interactives out of the tab order — the originals are the
    // canonical focus targets for keyboard users.
    clone.querySelectorAll('a, button, iframe, video, [tabindex]').forEach((el) => {
      el.setAttribute('tabindex', '-1');
    });
    return clone;
  }

  // Click-and-drag horizontal scrolling for mouse users. Mouse wheel scrolls
  // vertically by default and the hidden scrollbar leaves no visible
  // affordance, so without this the scroller is effectively unreachable.
  setupPointerDrag() {
    let startX = 0;
    let startScroll = 0;
    let pointerId = null;
    let moved = false;

    const onPointerDown = (e) => {
      if (e.pointerType !== 'mouse') return;
      if (e.target.closest('a, button, video, iframe, [contenteditable]')) return;
      pointerId = e.pointerId;
      startX = e.clientX;
      startScroll = this.scroller.scrollLeft;
      moved = false;
      this.scroller.setPointerCapture(pointerId);
      this.scroller.setAttribute('data-dragging', 'true');
    };

    const onPointerMove = (e) => {
      if (pointerId === null || e.pointerId !== pointerId) return;
      const dx = e.clientX - startX;
      if (Math.abs(dx) > 3) moved = true;
      this.scroller.scrollLeft = startScroll - dx;
    };

    const endDrag = (e) => {
      if (pointerId === null || (e && e.pointerId !== pointerId)) return;
      try {
        this.scroller.releasePointerCapture(pointerId);
      } catch (_) {}
      pointerId = null;
      this.scroller.removeAttribute('data-dragging');
      if (moved) {
        this._lastDragAt = Date.now();
        this.scroller.addEventListener(
          'click',
          (ev) => {
            ev.preventDefault();
            ev.stopPropagation();
          },
          { capture: true, once: true }
        );
      }
    };

    this.scroller.addEventListener('pointerdown', onPointerDown);
    this.scroller.addEventListener('pointermove', onPointerMove);
    this.scroller.addEventListener('pointerup', endDrag);
    this.scroller.addEventListener('pointercancel', endDrag);
  }

  // Translate vertical wheel input into horizontal scroll, but only when the
  // user has clearly engaged the gallery (shift held, or has interacted via
  // drag in the last few seconds). Without this guard the infinite loop would
  // swallow every vertical wheel event and trap the user inside the section.
  setupWheelToHorizontal() {
    this.scroller.addEventListener(
      'wheel',
      (e) => {
        if (e.deltaX !== 0) return;
        if (e.deltaY === 0) return;
        const max = this.scroller.scrollWidth - this.scroller.clientWidth;
        if (max <= 0) return;
        const engaged = e.shiftKey || (this._lastDragAt && Date.now() - this._lastDragAt < 1500);
        if (!engaged) return;
        e.preventDefault();
        this.scroller.scrollLeft += e.deltaY;
      },
      { passive: false }
    );
  }

  // Lazy video: the markup ships inside an inert <template>, so nothing is
  // fetched until the visitor presses play. Delegated on the scroller so the
  // cloned loop items are covered too. The drag handler skips pointerdowns on
  // <button>, and its post-drag click suppressor runs in the capture phase, so
  // a drag never triggers playback.
  setupLazyVideo() {
    this.scroller.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-bss-play]');
      if (!trigger) return;
      e.preventDefault();
      const item = trigger.closest('[data-bss-item]');
      if (item) this.activateVideo(item);
    });
  }

  activateVideo(item) {
    if (item.hasAttribute('data-bss-activated')) return;
    const template = item.querySelector('[data-bss-template]');
    const media = item.querySelector('[data-bss-media]');
    if (!template || !media) return;

    // Swap poster + play button for the real media. Cloning the template's
    // content is what moves it into the live DOM and starts the download.
    media.replaceChildren(template.content.cloneNode(true));
    item.setAttribute('data-bss-activated', '');

    const video = media.querySelector('video');
    if (video) {
      const attempt = video.play();
      if (attempt && typeof attempt.catch === 'function') attempt.catch(() => {});
    }
  }

  onScroll() {
    clearTimeout(this._scrollTimer);
    this._scrollTimer = setTimeout(() => {
      this.maybeRebase();
      this.syncActiveFromScroll();
    }, 80);
  }

  // If the closest item is in a clone zone, jump silently to its equivalent
  // in the middle (original) copy. The visual under the user's eye doesn't
  // change because the clone and the original render identically.
  maybeRebase() {
    if (!this.canLoop) return;
    const closestIdx = this.getClosestItemIndex();
    const N = this.originalCount;
    let targetIdx = null;
    if (closestIdx < N) targetIdx = closestIdx + N;
    else if (closestIdx >= 2 * N) targetIdx = closestIdx - N;
    if (targetIdx === null) return;

    const target = this.items[targetIdx];
    if (!target) return;
    const desired = this.centeredScrollLeftFor(target);

    const prevBehavior = this.scroller.style.scrollBehavior;
    this.scroller.style.scrollBehavior = 'auto';
    this.scroller.scrollLeft = desired;
    requestAnimationFrame(() => {
      this.scroller.style.scrollBehavior = prevBehavior;
    });
  }

  getClosestItemIndex() {
    const center = this.scroller.scrollLeft + this.scroller.clientWidth / 2;
    let closest = 0;
    let closestDist = Infinity;
    this.items.forEach((item, idx) => {
      const c = item.offsetLeft + item.clientWidth / 2;
      const d = Math.abs(c - center);
      if (d < closestDist) {
        closestDist = d;
        closest = idx;
      }
    });
    return closest;
  }

  centeredScrollLeftFor(item) {
    return item.offsetLeft - (this.scroller.clientWidth - item.clientWidth) / 2;
  }

  syncActiveFromScroll() {
    const closestIdx = this.getClosestItemIndex();
    const N = this.originalCount;
    const logical = this.canLoop ? ((closestIdx - N) % N + N) % N : closestIdx;
    this.setActive(logical);
  }

  setActive(idx) {
    if (idx === this.activeIndex) return;
    this.activeIndex = idx;
    this.dots.forEach((dot, i) => dot.toggleAttribute('aria-current', i === idx));
  }

  // Dot click: scroll to the equivalent item in whichever copy is closest to
  // the current view so the smooth-scroll distance stays short.
  scrollToLogicalIndex(logicalIdx) {
    if (!this.canLoop) {
      const item = this.items[logicalIdx];
      if (item) this.scroller.scrollTo({ left: this.centeredScrollLeftFor(item), behavior: 'smooth' });
      return;
    }
    const N = this.originalCount;
    const closestIdx = this.getClosestItemIndex();
    const currentCopy = Math.min(2, Math.max(0, Math.floor(closestIdx / N)));
    const item = this.items[currentCopy * N + logicalIdx];
    if (!item) return;
    this.scroller.scrollTo({ left: this.centeredScrollLeftFor(item), behavior: 'smooth' });
  }

  jumpToOriginalIndex(logicalIdx) {
    const N = this.originalCount;
    const domIdx = this.canLoop ? N + logicalIdx : logicalIdx;
    const item = this.items[domIdx];
    if (!item) return;
    const prev = this.scroller.style.scrollBehavior;
    this.scroller.style.scrollBehavior = 'auto';
    this.scroller.scrollLeft = this.centeredScrollLeftFor(item);
    requestAnimationFrame(() => {
      this.scroller.style.scrollBehavior = prev;
    });
  }
}

if (!customElements.get('brand-system-showcase')) {
  customElements.define('brand-system-showcase', BrandSystemShowcase);
}
